import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as LucideIcons from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { useAppContext } from '../../AppContext';
import './Visions.css';

import LoadingSpinner from '../LoadingSpinner';
import { ReportModal } from '../ReportModal';
import CommentModal from '../CommentModal';

const VisionViewer = ({ visions, startIndex, onNextUser, onPrevUser, onClose, handleUserSelect }) => {
    const { t } = useTranslation();
    const { appFunctions, userId, showConfirmation } = useAppContext();
    const [currentUserIndex, setCurrentUserIndex] = useState(startIndex || 0);
    const [currentItemIndex, setCurrentItemIndex] = useState(0);
    const [isPaused, setIsPaused] = useState(false);

    const videoRef = useRef(null);
    const audioRef = useRef(null);

    // State for optimistic updates on shared content
    const [isLiked, setIsLiked] = useState(false);
    const [likeCount, setLikeCount] = useState(0);
    const [amplifyCount, setAmplifyCount] = useState(0);
    const [commentCount, setCommentCount] = useState(0);

    const [replyText, setReplyText] = useState('');

    // State for modals
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportContent, setReportContent] = useState(null);
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [showOptionsMenu, setShowOptionsMenu] = useState(false);

    const currentUserVisions = visions[currentUserIndex];
    const currentItem = currentUserVisions?.items[currentItemIndex];

    // Effect for handling audio playback
    useEffect(() => {
        const videoEl = videoRef.current;
        const audioEl = audioRef.current;

        if (videoEl) {
            videoEl.volume = (currentItem?.audioSettings?.originalVolume || 100) / 100;
        }

        if (audioEl && currentItem?.music?.trackId) {
            // For now, using placeholder URLs. In a real app, you'd map trackId to a URL.
            audioEl.src = `/music/${currentItem.music.trackId}.mp3`;
            audioEl.volume = (currentItem.music.volume || 100) / 100;
        } else if (audioEl) {
            audioEl.src = '';
        }

        if (isPaused) {
            videoEl?.pause();
            audioEl?.pause();
        } else {
            videoEl?.play().catch(e => console.error("Video play failed", e));
            if (audioEl?.src) {
                audioEl?.play().catch(e => console.error("Audio play failed", e));
            }
        }
    }, [currentItem, isPaused]);

    // Effect to initialize/reset optimistic state when the item changes
    useEffect(() => {
        if (currentItem && currentItem.sharedContent) {
            setLikeCount(currentItem.sharedContent.likesCount || 0);
            setAmplifyCount(currentItem.sharedContent.echoesInvested || 0);
            setCommentCount(currentItem.sharedContent.commentsCount || 0);
            setIsLiked(currentItem.sharedContent.likes?.includes(userId) || false);
        }
        setShowOptionsMenu(false); // Close options menu when item changes

        if (currentItem) {
            const recordView = httpsCallable(appFunctions, 'recordVisionView');
            recordView({ visionId: currentItem.id, creatorId: currentUserVisions.userId });
        }
    }, [currentItem, userId, appFunctions, currentUserVisions.userId]);

    const handleSendReply = async () => {
        if (!replyText.trim()) return;

        const sendVisionReply = httpsCallable(appFunctions, 'sendVisionReply');
        try {
            await sendVisionReply({
                visionId: currentItem.id,
                creatorId: currentUserVisions.userId,
                replyText: replyText,
            });
            setReplyText('');
            // Optionally, show a confirmation toast/message
        } catch (error) {
            console.error("Error sending vision reply:", error);
            // Optionally, show an error toast/message
        }
    };

    // Navigation Logic
    const handleNextItem = () => {
        if (currentItemIndex < currentUserVisions.items.length - 1) {
            setCurrentItemIndex(currentItemIndex + 1);
        } else if (currentUserIndex < visions.length - 1) {
            onNextUser();
            setCurrentItemIndex(0);
        } else {
            onClose();
        }
    };

    const handlePrevItem = () => {
        if (currentItemIndex > 0) {
            setCurrentItemIndex(currentItemIndex - 1);
        } else if (currentUserIndex > 0) {
            onPrevUser();
            setCurrentItemIndex(visions[currentUserIndex - 1]?.items.length - 1 || 0);
        }
    };

    // Auto-advance timer
    useEffect(() => {
        if (!currentItem || isPaused) return;
        const timer = setTimeout(handleNextItem, currentItem.duration || 7000);
        return () => clearTimeout(timer);
    }, [currentItem, currentItemIndex, currentUserIndex, isPaused, handleNextItem]);
    
    useEffect(() => {
        setCurrentUserIndex(startIndex || 0);
        setCurrentItemIndex(0);
    }, [startIndex]);

    const handleLikeToggle = async () => {
        if (!currentItem.sharedContent) return;

        const originalLiked = isLiked;
        const originalLikeCount = likeCount;
        
        setIsLiked(!originalLiked);
        setLikeCount(originalLiked ? originalLikeCount - 1 : originalLikeCount + 1);

        const updateReaction = httpsCallable(appFunctions, 'updateReaction');
        try {
            await updateReaction({
                entryId: currentItem.sharedContent.id,
                authorId: currentItem.sharedContent.authorId,
                reaction: 'like',
                contentType: currentItem.sharedContent.type,
            });
        } catch (error) {
            console.error("Error toggling like:", error);
            setIsLiked(originalLiked);
            setLikeCount(originalLikeCount);
        }
    };

    const handleAmplifyVision = () => {
        if (!currentItem.sharedContent || !currentItem.sharedContent.id) return;

        showConfirmation({
            message: t('visions.viewer.amplifyConfirm', { cost: 10 }),
            onConfirm: async () => {
                const originalAmplifyCount = amplifyCount;
                setAmplifyCount(originalAmplifyCount + 10);

                const amplifySharedVision = httpsCallable(appFunctions, 'amplifySharedVision');
                try {
                    await amplifySharedVision({
                        visionId: currentUserVisions.id,
                        itemId: currentItem.id,
                        sharedContentId: currentItem.sharedContent.id,
                        sharedContentType: currentItem.sharedContent.type,
                        amount: 10
                    });
                } catch (error) {
                    console.error("Error amplifying vision:", error);
                    setAmplifyCount(originalAmplifyCount);
                }
            }
        });
    };

    const handleReport = () => {
        if (!currentItem.sharedContent) return;
        setReportContent({ 
            contentId: currentItem.sharedContent.id, 
            contentType: currentItem.sharedContent.type, 
            authorId: currentItem.sharedContent.authorId 
        });
        setShowReportModal(true);
        setShowOptionsMenu(false);
    };
    
    const handlePanEnd = (event, info) => {
        const swipeThreshold = 50;
        if (info.offset.x > swipeThreshold) onPrevUser();
        else if (info.offset.x < -swipeThreshold) onNextUser();
    };

    const handleStickerReact = async (stickerIndex) => {
        const reactToVisionSticker = httpsCallable(appFunctions, 'reactToVisionSticker');
        try {
            await reactToVisionSticker({
                visionId: currentItem.id,
                creatorId: currentUserVisions.userId,
                stickerIndex,
            });
            // Optionally show some feedback to the user
        } catch (error) {
            console.error("Error reacting to sticker:", error);
        }
    };

    if (!currentItem) {
        return <div className="fixed inset-0 bg-black z-50 flex items-center justify-center"><LoadingSpinner /></div>;
    }

    return (
        <>
            {showReportModal && <ReportModal {...reportContent} onClose={() => setShowReportModal(false)} />}
            {showCommentModal && (
                <CommentModal 
                    post={{...currentItem.sharedContent, id: currentItem.sharedContent.id}} 
                    onClose={() => setShowCommentModal(false)} 
                    contentType={currentItem.sharedContent.type} 
                />
            )}
            <div 
                className="fixed inset-0 bg-black z-50 flex items-center justify-center"
                onMouseDown={() => setIsPaused(true)} onMouseUp={() => setIsPaused(false)}
                onTouchStart={() => setIsPaused(true)} onTouchEnd={() => setIsPaused(false)}
            >
                <AnimatePresence>
                    <motion.div
                        key={`${currentUserIndex}-${currentItemIndex}`}
                        className="relative w-full h-full max-w-md max-h-screen bg-black"
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        onPanEnd={handlePanEnd}
                    >
                        {/* Progress Bars */}
                        <div className="absolute top-2 left-2 right-2 flex items-center z-20 gap-1">
                            {currentUserVisions.items.map((_, index) => (
                                <div key={index} className="h-1 bg-white/30 flex-1 rounded-full">
                                    {index < currentItemIndex && <div className="h-1 bg-white rounded-full" />}
                                    {index === currentItemIndex && (
                                        <motion.div
                                            className="h-1 bg-white rounded-full"
                                            initial={{ width: '0%' }} animate={{ width: '100%' }}
                                            transition={{ duration: isPaused ? 0 : (currentItem.duration || 7000) / 1000, ease: 'linear' }}
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                        <div className="header-gradient" />
                                        
                        {/* Header */}
                        <div className="absolute top-4 left-4 flex items-center z-20">
                            <div className="flex items-center cursor-pointer" onClick={(e) => { e.stopPropagation(); handleUserSelect(currentUserVisions.userId); }}>
                                <img src={currentUserVisions.userPhotoURL || 'https://placehold.co/40x40/AEC6CF/FFFFFF?text=U'} alt={currentUserVisions.userDisplayName || 'Anonymous'} className="w-10 h-10 rounded-full mr-2" />
                                <span className="text-white font-bold">{currentUserVisions.userDisplayName || 'Anonymous'}</span>
                            </div>
                        </div>
                                                            
                        {/* Close & Options Button */}
                        <div className="absolute top-4 right-4 flex items-center z-20">
                            {currentItem.sharedContent && (
                                <div className="relative">
                                    <button onClick={(e) => { e.stopPropagation(); setShowOptionsMenu(prev => !prev); }} className="text-white bg-black/50 rounded-full p-1 hover:bg-black/70 transition-colors mr-2">
                                        <LucideIcons.MoreVertical size={28} />
                                    </button>
                                    {showOptionsMenu && (
                                        <div className="absolute top-full right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg z-30">
                                            <button onClick={(e) => { e.stopPropagation(); handleReport(); }} className="flex items-center w-full px-4 py-2 text-left text-white hover:bg-gray-700">
                                                <LucideIcons.Flag size={18} className="mr-2" /> {t('common.report')}
                                            </button>
                                            {currentUserVisions.userId === userId && (
                                                <button onClick={(e) => {
                                                    e.stopPropagation();
                                                    showConfirmation({
                                                        message: t('visions.viewer.archiveConfirm'),
                                                        onConfirm: async () => {
                                                            const archiveVision = httpsCallable(appFunctions, 'archiveVisionAsHighlight');
                                                            try {
                                                                await archiveVision({ visionId: currentItem.id, creatorId: currentUserVisions.userId });
                                                                alert(t('visions.viewer.archiveSuccess'));
                                                                onClose();
                                                            } catch (error) {
                                                                console.error("Error archiving vision:", error);
                                                                alert(t('visions.viewer.archiveError', { message: error.message }));
                                                            }
                                                        }
                                                    });
                                                    setShowOptionsMenu(false);
                                                }} className="flex items-center w-full px-4 py-2 text-left text-white hover:bg-gray-700">
                                                    <LucideIcons.Archive size={18} className="mr-2" /> {t('visions.viewer.archiveButton')}
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                            <button onClick={(e) => { e.stopPropagation(); onClose(); }} className="text-white bg-black/50 rounded-full p-1 hover:bg-black/70 transition-colors">
                                <LucideIcons.X size={28} />
                            </button>
                        </div>
                        
                        {/* Content */}
                        <div className="relative w-full h-full flex items-center justify-center">
                            <audio ref={audioRef} loop />
                            {currentItem.type === 'image' && <img src={currentItem.contentURL} alt="" className="max-h-full max-w-full" />}
                            {currentItem.type === 'video' && (
                                <video ref={videoRef} src={currentItem.contentURL} autoPlay playsInline loop className="w-full h-full object-contain" />
                            )}
                            {currentItem.type === 'text' && (
                                <div className="w-full h-full flex items-center justify-center p-8" style={{ backgroundColor: currentItem.backgroundColor || '#000' }}>
                                    <p className="text-white text-2xl font-bold text-center">{currentItem.text}</p>
                                </div>
                            )}
                            {/* Render Text Overlays */}
                            {currentItem.textOverlays && currentItem.textOverlays.map((overlay, index) => (
                                <div
                                    key={index}
                                    className="absolute"
                                    style={{
                                        left: `${overlay.position.x}%`,
                                        top: `${overlay.position.y}%`,
                                        color: overlay.color,
                                        transform: 'translate(-50%, -50%)', // Center the text
                                        pointerEvents: 'none', // Make text non-interactive
                                        textShadow: '2px 2px 4px rgba(0,0,0,0.7)' // Add shadow for readability
                                    }}
                                >
                                    <span style={{ fontSize: '24px', fontWeight: 'bold' }}>{overlay.text}</span>
                                </div>
                            ))}
                            {/* Render Stickers */}
                            {currentItem.stickers && currentItem.stickers.map((sticker, index) => (
                                <div
                                    key={index}
                                    className="absolute text-4xl cursor-pointer"
                                    style={{
                                        left: `${sticker.position.x}%`,
                                        top: `${sticker.position.y}%`,
                                        transform: 'translate(-50%, -50%)',
                                    }}
                                    onClick={() => handleStickerReact(index)}
                                >
                                    {sticker.emoji}
                                </div>
                            ))}
                        </div>

                        {/* Bottom Controls */}
                        <div className="absolute bottom-4 left-0 right-0 px-4 z-20">
                            <div className="flex items-center w-full">
                                {/* Reply Input */}
                                <input
                                    type="text"
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder={t('visions.viewer.replyPlaceholder')}
                                    className="flex-grow bg-black/50 border border-white/30 rounded-full px-4 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <button onClick={handleSendReply} className="ml-2 text-white">
                                    <LucideIcons.Send size={24} />
                                </button>
                                {currentUserVisions.userId === userId && (
                                    <button onClick={() => alert('Viewers list coming soon!')} className="ml-2 text-white">
                                        <LucideIcons.Eye size={24} />
                                    </button>
                                )}
                            </div>

                            {/* Shared Content Controls */}
                            {currentItem.sharedContent && (
                                <div className="flex justify-center items-center space-x-8 mt-4">
                                    <button onClick={(e) => { e.stopPropagation(); handleLikeToggle(); }} className="flex items-center gap-2 text-white transition-transform duration-200 ease-in-out hover:scale-110">
                                        <LucideIcons.Heart size={28} className={`transition-colors duration-200 ${isLiked ? 'text-red-500 fill-current' : 'text-white'}`} />
                                        <span className="font-semibold text-lg">{likeCount}</span>
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); handleAmplifyVision(); }} className="flex items-center gap-2 text-white transition-transform duration-200 ease-in-out hover:scale-110">
                                        <LucideIcons.Flame size={28} className="text-orange-400" />
                                        <span className="font-semibold text-lg">{amplifyCount}</span>
                                    </button>
                                    <button onClick={(e) => { e.stopPropagation(); setShowCommentModal(true); }} className="flex items-center gap-2 text-white transition-transform duration-200 ease-in-out hover:scale-110">
                                        <LucideIcons.MessageCircle size={28} />
                                        <span className="font-semibold text-lg">{commentCount}</span>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Tap Navigation Overlays */}
                        <div className="absolute inset-y-0 left-0 w-1/3 z-10" onClick={(e) => { e.stopPropagation(); handlePrevItem(); }} />
                        <div className="absolute inset-y-0 right-0 w-1/3 z-10" onClick={(e) => { e.stopPropagation(); handleNextItem(); }} />
                    </motion.div>
                </AnimatePresence>
            </div>
        </>
    );
};


export default VisionViewer;